<template>
	<div class="sidebar" :class="{'with-steps-sheet': funnelStore.showStepsBottomSheet, user: isUserSection, manual: userStore.manualCode}">
		<div class="container-logo" @click="reloadApp">
			<img src="https://img.qromo.io/img/vue/logo/rgb/sign-white.svg" alt="logo white">
		</div>
		<div class="container-sidebar-buttons">
			<div class="mask">
				<div v-for="(button, index) in buttons" v-show="button.show" :class="{'top-auto': button.topAuto, 'underline': button.underline}" class="sidebar-button-loop" :key="index">
					<SidebarMacroButton
						:icon-name="button.iconName"
						:icon-color="button.iconColor ? button.iconColor : null"
						:active="isButtonActive(button)"
						:locked="!!button.locked"
						:warning="!!button.warning"
						@button-click="buttonClick(index, button)" />
				</div>
			</div>
		</div>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="businessPickerActive" @toggling-popup="togglingBusinessPicker">
			<BusinessPicker v-if="businessPickerActive" @done="businessPickerActive = false" />
		</AppBottomSheet>
	</div>
</template>

<script>
import SidebarMacroButton from "@/console-new/components/shared/SidebarMacroButton.vue";
import BusinessPicker from "@/console-new/components/shared/BusinessPicker.vue";
import {useUserStore, useFunnelStore, useMarketplaceStore, useOpenAIChatsStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";
import {useSuperStore} from "@/user/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import { hasPermissionForRoute, isRouteLockedByPlan, handleLockedRoute } from "@/console-new/helpers/permissions";

export default {
	setup() {
		const userStore = useUserStore();
		const superStore = useSuperStore();
		let marketplaceStore = useMarketplaceStore();
		let languageStore = useLanguageStore();
		let funnelStore = useFunnelStore();
		let openAIChatsStore = useOpenAIChatsStore();
		return {userStore, funnelStore, marketplaceStore, superStore, openAIChatsStore, getString: languageStore.getString}
	},
	name: "SidebarMacro",
	components: {SidebarMacroButton, BusinessPicker, AppBottomSheet},
	emits: ["switched-item"],
	mixins: [WindowWidth],
	data() {
		return {
			businessPickerActive: false,
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		},
		isUserSection() {
			return location && location.href && location.href.startsWith(URL_HTML + '/user');
		},
		plans() {
			if(this.userStore && this.userStore.currPlans) {
				return this.userStore.currPlans;
			}
			return {};
		},
		userIsSubscribed() {
			return this.userStore?.currSubscription && this.userStore?.currBusiness?.plan_id;
		},
		hasStripeDeadlineWarning() {
			return this.userStore.currBusiness &&
				!!this.userStore.currBusiness.stripe_account &&
				this.userStore.currBusiness.stripe_account_type === "custom" &&
				this.userStore.currBusiness.stripe_account_deadline
		},
		buttons() {
			return this.isUserSection ? this.userButtons : this.consoleButtons;
		},
		consoleButtons() {
			return [
				{
					iconName: 'iconOperations',
					route: '/ops',
					show: hasPermissionForRoute('/ops'),
					locked: isRouteLockedByPlan('/ops'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					underline: true
				},
				{
					iconName: 'iconAnalytics',
					route: '/analytics',
					locked: isRouteLockedByPlan('/analytics'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					show: hasPermissionForRoute('/analytics'),
				},
				{
					iconName: 'iconMarketing',
					route: '/marketing',
					locked: isRouteLockedByPlan('/marketing'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					show: hasPermissionForRoute('/marketing'),
				},
				{
					iconName: 'iconSettings',
					route: '/config',
					show: hasPermissionForRoute('/config'),
					locked: isRouteLockedByPlan('/config'),
					lockedClick: ()=>{handleLockedRoute(this.$router)},
					warning: this.hasStripeDeadlineWarning
				},
				{
					iconName: 'iconPlugins',
					route: '/plugins',
					show: hasPermissionForRoute('/plugins'),
				},
				{
					iconName: 'iconHelpCenter',
					route: '/help',
					show: hasPermissionForRoute('/help'),
					locked: isRouteLockedByPlan('/help'),
					topAuto: true,
					lockedClick: ()=>{handleLockedRoute(this.$router)},
				},
				{
					id: 'user-console',
					iconName: this.getUserConsoleIcon(),
					show: true,
					topAuto: !(hasPermissionForRoute('/help')) //should be true if /help is not there
				}
			]
		},
		userButtons() {
			let superIcon;
			if(this.superStore.isUltra || this.userStore?.user?.type === 'ultra') {
				superIcon = 'iconUltra';
			}
			else if(this.superStore.isMega || this.userStore?.user?.type === 'mega') {
				superIcon = 'iconMega';
			} else {
				superIcon = 'iconSuper';
			}
			return [
				{
					iconName: 'iconAnalytics',
					route: '/analytics',
					show: true,
				},
				{
					iconName: 'iconManagement',
					route: '/businesses',
					show: true,
				},
				{
					iconName: 'iconSettings',
					route: '/settings',
					show: true,
				},
				{
					iconName: superIcon,
					route: '/super',
					locked: false,
					show: this.superStore.isSuper || this.userStore?.user?.type === 'super' || this.userStore?.user?.type === 'mega' || this.userStore?.user?.type === 'ultra',
				},
				{
					id: 'business-console',
					iconName: 'iconBusinessConsole',
					show: true,
					topAuto: true
				},
			]
		}
	},
	methods: {
		reloadApp() {
			this.$router.push('/').then(() => {
				window.location.reload();
			});
		},
		isButtonActive(button) {
			if(button.route === '/help') {
				return this.openAIChatsStore.drawerOpen;
			}
			return this.$route && this.$route.path && button.route && this.$route.path.startsWith(button.route);
		},
		getUserConsoleIcon() {
			return this.userStore.user && this.userStore.user.picture
				? this.userStore.user.picture
				: 'iconUser';
		},
		togglingBusinessPicker(active) {
			if(!active) {
				this.businessPickerActive = false;
			}
		},
		buttonClick(index, button) {
			if(this.buttons[index].locked) {
				if(this.buttons[index].lockedClick) {
					this.buttons[index].lockedClick();
				}
			} else {
				if(button.id === 'old-console') {
					sessionStorage.setItem('force-old-console', '1');
					window.location.href = URL_HTML + '/console';
				} else if(button.id === 'user-console') {
					this.businessPickerActive = true;
				} else if(button.id === 'business-console') {
					window.location.href = URL_HTML + '/console/new/config';
				} else if(button.route === '/help') {
					let wasOpen = this.openAIChatsStore.drawerOpen;
					this.openAIChatsStore.toggleDrawer();
					if(!wasOpen) {
						this.$emit('switched-item');
					}
					return;
				} else if(button.route) {
					this.$router.push(button.route)
				}
				this.$emit('switched-item');
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.sidebar {
	width: $sidebarMacroWidth;
	height: 100%;
	background-color: var(--color-bg-sidebar);
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	z-index: 200;
	position: relative;

	&.manual {
		background-color: var(--color-manual-1);
	}

	&.user {
		background-color: var(--color-primary);

		&:deep(.sidebar-macro-button-container:after) {
			background-color:var(--color-primary);
		}
	}

	.container-logo {
		display: flex;
		justify-content: center;
		align-items: center;
		user-select: none;
		cursor: pointer;

		img {
			width: 100%;
		}
	}

	.container-sidebar-buttons {
		@include hideScrollbar;
		overflow-y: auto;
		display: flex;
		margin-top: 15px;
		width: calc(100% + 10px);
		height: calc(100% - 15px - 50px);
		flex-direction: column;
		justify-content: flex-start;
		pointer-events: none;

		.mask {
			position: relative;
			display: flex;
			width: 100%;
			height: 100%;
			flex-direction: column;
			justify-content: flex-start;

			.sidebar-button-loop {
				position: relative;
				width: 100%;
				display: flex;
				justify-content: center;
				align-items: center;
				margin-bottom: 8px;
				&.underline {
					margin-bottom: 16px;
				}
				&.underline:after {
					content: '';
					width: calc(100% - 28px);
					height: 1px;
					background-color: rgba(255, 255, 255, 0.4);
					position: absolute;
					bottom: -8px;
				}

				&.top-auto {
					margin-top: auto;
				}
			}
		}
	}

	@media (max-width: 920px) {
		&.with-steps-sheet {
			height: calc(100% - 55px);
		}
	}

}
</style>